/**
 * useDownloadPdf Hook
 *
 * Provides functionality to download PDF with embedded annotations
 * Excludes signing layer data (only includes comments/annotations)
 */

import { useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

export interface UseDownloadPdfOptions {
  pdfUrl: string;
  pdfDocument: pdfjsLib.PDFDocumentProxy | null;
  documentTitle: string;
  annotations?: any[];  // Custom annotations to embed (comments, strikeouts, free text)
}

export const useDownloadPdf = ({
  pdfUrl,
  pdfDocument,
  documentTitle,
  annotations = [],
}: UseDownloadPdfOptions) => {
  const downloadPdf = useCallback(async () => {
    try {
      // Fetch the original PDF
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename from document title
      const fileName = documentTitle.endsWith('.pdf')
        ? documentTitle
        : `${documentTitle}.pdf`;

      link.download = fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      throw error;
    }
  }, [pdfUrl, documentTitle, annotations]);

  return { downloadPdf };
};
